import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useAdmin } from '../../context/adminContext';
import {
    LayoutDashboard,
    Users,
    CreditCard,
    LogOut,
    Menu,
    X,
    ChevronRight,
    Search,
    Bell,
    UserCircle,
    Book,
    Wallet,
    Package,
    Landmark,
    Home
} from 'lucide-react';

const DashboardLayout = () => {
    // Default to closed on mobile (< 1024px), open on desktop
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
    const [expandedMenu, setExpandedMenu] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const { admin, logout, loading } = useAdmin(); // Get admin context

    // Close sidebar automatically on mobile when route changes
    useEffect(() => {
        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false);
        }
    }, [location.pathname]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
            } else {
                // Close on mobile resize
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const profileMenuRef = useRef(null);

    // Close profile menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Menu Items
    const menuItems = [
        { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, exact: true },
        { path: '/dashboard/daily-transaction', label: 'Daily Transaction', icon: Wallet },
        {
            label: 'Rojmi Management',
            icon: Book,
            children: [
                { path: '/dashboard/rojmi', label: 'Day Book' },
                { path: '/dashboard/rojmi/udhar', label: 'Udhar (Credit)' }
            ]
        },
        { path: '/dashboard/parties', label: 'Parties & Vendors', icon: Users },
        { path: '/dashboard/accounts', label: 'Bank Accounts', icon: Landmark },
        { path: '/dashboard/parcels', label: 'Parcel Management', icon: Package },
        { path: '/dashboard/bhishi', label: 'Bhishi Management', icon: Users },
        { path: '/dashboard/employees', label: 'Employee', icon: UserCircle },
    ];

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    // Get current page title based on path
    const getPageTitle = () => {
        // Flatten menu items for search
        const flatItems = menuItems.flatMap(item => item.children ? item.children : item);

        const current = flatItems.find(item =>
            item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path)
        );
        if (current) return current.label;
        if (location.pathname.includes('/bills/')) return 'Bill Management';
        return 'Dashboard';
    };

    const getInitials = (name) => {
        return name ? name.charAt(0).toUpperCase() : "A";
    };

    // Strict Auth Guard
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!admin) {
        return <Navigate to="/" replace />; // Immediate redirect if not authenticated
    }




    return (
        <div className="h-screen overflow-hidden bg-gray-50 flex font-sans text-slate-800">
            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div
                    onClick={() => setIsSidebarOpen(false)}
                    className="fixed inset-0 bg-slate-900/60 z-40 lg:hidden backdrop-blur-sm transition-all duration-300"
                />
            )}

            {/* --- SIDEBAR --- */}
            <aside
                onMouseEnter={() => window.innerWidth >= 1024 && setIsSidebarOpen(true)}
                onMouseLeave={() => window.innerWidth >= 1024 && setIsSidebarOpen(false)}
                className={`fixed inset-y-0 left-0 z-50 bg-[#0f172a] text-white transition-all duration-300 ease-out shadow-2xl overflow-x-hidden border-r border-slate-700/50
                ${isSidebarOpen ? 'translate-x-0 w-72' : '-translate-x-full lg:translate-x-0 lg:w-20'} 
                lg:relative flex flex-col`}
            >
                {/* Brand */}
                <div className="h-20 flex items-center justify-center border-b border-slate-800/80 bg-[#0f172a]/50 backdrop-blur-md">
                    <div className="flex items-center gap-3 overflow-hidden px-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex flex-shrink-0 items-center justify-center text-white shadow-lg shadow-blue-500/20 ring-1 ring-white/10">
                            <span className="font-bold text-xl">S</span>
                        </div>
                        <span className={`font-bold text-lg tracking-wide bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent whitespace-nowrap transition-all duration-300 ${isSidebarOpen ? 'opacity-100 max-w-[200px]' : 'opacity-0 max-w-0'}`}>
                            Sakshi Collection
                        </span>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {menuItems.map((item, index) => {
                        const Icon = item.icon;
                        let isActive = false;
                        if (item.children) {
                            isActive = item.children.some(child => location.pathname.startsWith(child.path));
                        } else {
                            isActive = item.exact
                                ? location.pathname === item.path
                                : location.pathname.startsWith(item.path);
                        }

                        if (item.children) {
                            const isExpanded = expandedMenu === index || isActive;
                            return (
                                <div key={index} className="space-y-1">
                                    <button
                                        onClick={() => setExpandedMenu(expandedMenu === index ? null : index)}
                                        className={`w-full flex items-center justify-between px-3.5 py-3 rounded-xl transition-all duration-200 group relative
                                            ${isActive
                                                ? 'bg-slate-800/50 text-white shadow-inner'
                                                : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-100'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-3.5 overflow-hidden">
                                            <Icon size={22} className={`shrink-0 transition-colors ${isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-blue-400'}`} />
                                            <span className={`font-medium whitespace-nowrap transition-all duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'}`}>
                                                {item.label}
                                            </span>
                                        </div>
                                        {isSidebarOpen && (
                                            <ChevronRight size={16} className={`text-slate-500 transition-transform duration-300 ${isExpanded ? 'rotate-90 text-blue-400' : ''}`} />
                                        )}
                                    </button>

                                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded && isSidebarOpen ? 'max-h-60 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                                        <div className="ml-5 pl-4 border-l-2 border-slate-700/50 space-y-1 py-1">
                                            {item.children.map((child) => {
                                                const isChildActive = location.pathname === child.path;
                                                return (
                                                    <NavLink
                                                        key={child.path}
                                                        to={child.path}
                                                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm block relative
                                                            ${isChildActive
                                                                ? 'text-white bg-blue-600/10 before:absolute before:left-[-1.15rem] before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-blue-500'
                                                                : 'text-slate-400 hover:text-white hover:bg-slate-800/30'
                                                            }
                                                        `}
                                                    >
                                                        <span className="truncate">{child.label}</span>
                                                    </NavLink>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.exact}
                                className={({ isActive }) => `
                                    flex items-center gap-3.5 px-3.5 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden
                                    ${isActive
                                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md shadow-blue-900/40'
                                        : 'text-slate-400 hover:bg-slate-800/40 hover:text-slate-100'
                                    }
                                `}
                            >
                                <Icon size={22} strokeWidth={isActive ? 2.5 : 2} className={`shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-105'}`} />
                                <span className={`font-medium whitespace-nowrap transition-all duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'}`}>
                                    {item.label}
                                </span>
                                {isActive && (
                                    <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-white/50 animate-pulse" />
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                {/* User Info / Footer */}
                {admin && isSidebarOpen && (
                    <div className="p-4 border-t border-slate-800/80 bg-[#0f172a]">
                        <div className="flex items-center gap-3 p-2 rounded-xl bg-slate-800/40 border border-slate-700/30">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-300 font-bold border border-indigo-500/30">
                                {getInitials(admin.name)}
                            </div>
                            <div className="overflow-hidden">
                                <p className="text-sm font-medium text-slate-200 truncate">{admin.name}</p>
                                <p className="text-xs text-slate-500 truncate">Administrator</p>
                            </div>
                        </div>
                    </div>
                )}
            </aside>

            {/* --- MAIN CONTENT WRAPPER --- */}
            <div className="flex-1 flex flex-col min-w-0 bg-slate-50/50">

                {/* Header */}
                <header className="h-20 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-40 transition-shadow duration-300">
                    <div className="flex items-center gap-5">
                        <button
                            onClick={toggleSidebar}
                            className="p-2.5 hover:bg-slate-100 rounded-xl text-slate-600 transition-all hover:scale-105 active:scale-95"
                        >
                            <Menu size={22} />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent hidden md:block">
                                {getPageTitle()}
                            </h1>
                            <p className="text-xs text-slate-500 font-medium hidden md:block mt-0.5">
                                Welcome back, {admin?.name?.split(' ')[0]}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-6">
                        <Link
                            to="/"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors text-sm font-medium"
                        >
                            <Home size={18} />
                            <span className="hidden sm:inline">Go to Home</span>
                        </Link>
                        {/* Profile Dropdown */}
                        {admin && (
                            <div className="relative" ref={profileMenuRef}>
                                <button
                                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                                    className="flex items-center gap-2 focus:outline-none group"
                                >
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm ring-4 ring-white shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-all duration-300">
                                        {getInitials(admin.name)}
                                    </div>
                                    <ChevronRight size={16} className={`text-slate-400 transition-transform duration-200 ${showProfileMenu ? 'rotate-90' : 'rotate-0'}`} />
                                </button>

                                {showProfileMenu && (
                                    <div className="absolute right-0 mt-4 w-60 bg-white rounded-2xl shadow-xl shadow-slate-200/50 py-2 ring-1 ring-black/5 z-50 transform origin-top-right animate-in fade-in zoom-in-95 duration-200">
                                        <div className="px-5 py-3 border-b border-slate-100">
                                            <p className="font-semibold text-slate-800 truncate">{admin.name}</p>
                                            <p className="text-xs text-slate-500 truncate mt-0.5">{admin.email}</p>
                                        </div>
                                        <div className="py-2">
                                            <div className="h-px bg-slate-100 my-1 mx-4"></div>
                                            <button
                                                onClick={() => {
                                                    logout();
                                                    setShowProfileMenu(false);
                                                }}
                                                className="flex items-center px-5 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-colors font-medium"
                                            >
                                                <LogOut size={18} className="mr-3" />
                                                Sign Out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </header>

                {/* Content Area */}
                <main className="flex-1 p-6 lg:p-10 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-slate-300/50 scrollbar-track-transparent hover:scrollbar-thumb-slate-400/50 transition-colors">
                    <div className="max-w-[1600px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
